<template>
  <div :id="id">
    <transition
      v-bind="overlayTransitionProps"
      @after-leave="handleAfterLeave"
    >
      <div
        v-if="visible"
        :class="[
          $style.fixedContainer,
          $style.overlay,
          { [$style.dark]: isDark }
        ]"
      />
    </transition>
    <transition
      v-bind="modalTransitionProps"
      @after-enter="handleAfterEnter"
    >
      <div
        v-if="visible"
        :class="[$style.fixedContainer, $style.modalPositioning]"
        @mousedown.self.prevent="handleOverlayClick"
      >
        <slot />
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'ModalOverlay',

  inject: ['$validator'],

  props: {
    closeCallback: {
      type: Function,
      default: () => {},
    },
    handleAfterEnter: {
      type: Function,
      default: () => {},
    },
    handleAfterLeave: {
      type: Function,
      default: () => {},
    },
    id: {
      type: String,
      default: '',
    },
    isDark: {
      type: Boolean,
      default: false,
    },
    preventCloseOnOverlayClick: {
      type: Boolean,
      default: false,
    },
    visible: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    modalTransitionProps() {
      return {
        enterClass: this.$style.fadeModalEnter,
        enterActiveClass: this.$style.fadeModalTransitionEnter,
        leaveActiveClass: this.$style.fadeModalTransitionLeave,
        leaveToClass: this.$style.fadeModalLeave,
        tag: 'div',
      };
    },

    overlayTransitionProps() {
      return {
        enterClass: this.$style.fadeOverlay,
        enterActiveClass: this.$style.fadeOverlayTransitionEnter,
        leaveActiveClass: this.$style.fadeOverlayTransitionLeave,
        leaveToClass: this.$style.fadeOverlay,
        tag: 'div',
      };
    },
  },

  methods: {
    handleOverlayClick() {
      if (!this.preventCloseOnOverlayClick) {
        this.closeCallback();
      }
    },
  },
};
</script>

<style module>
@value (
  fadeTransition,
  fadeTransitionDelayed,
  fadeTransitionTransformDelayed
) from '~css/transitions.css';
@value (max-width-medium) from '~css/breakpoints.css';
@value (modalOverlayZIndex) from '~css/z-index.css';

.fixedContainer {
  bottom: 0;
  left: 0;
  position: fixed;
  right: 0;
  top: 0;
}

.overlay {
  background: rgba(0, 0, 0, 0.2);
  z-index: modalOverlayZIndex;
}

.dark {
  background: var(--jc-scrim-background);
}

.modalPositioning {
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  z-index: calc(modalOverlayZIndex + 1);
}

/**
 * These spacers are used to nudge the modal higher rather than being perfectly vertically centered
 * @see: https://www.bennadel.com/blog/3394-css-flexbox-aligning-content-slightly-off-center.html
 */
.modalPositioning::before {
  content: '';
  flex: 1 1 40%;
}

.modalPositioning::after {
  content: '';
  flex: 1 1 60%;
}

.fadeOverlay {
  opacity: 0;
}

.fadeOverlayTransitionEnter {
  transition: fadeTransition;
}

.fadeOverlayTransitionLeave {
  transition: fadeTransitionDelayed;
}

.fadeModalEnter {
  opacity: 0;
}

@media (max-width: max-width-medium) {
  .fadeModalEnter {
    transform: translate(-50%, -50%);
  }
}

.fadeModalLeave {
  opacity: 0;
}

.fadeModalTransitionEnter {
  transition: fadeTransitionTransformDelayed;
}

.fadeModalTransitionLeave {
  transition: fadeTransition;
}
</style>
